import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  School, ArrowRight, CheckCircle, Users, Calendar,
  CreditCard, Bell, Bus, BookOpen, ClipboardList,
  BarChart3, Shield, Clock, Globe, Zap, ChevronDown,
  Play, GraduationCap, Building2
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  ScrollReveal,
  StaggerContainer,
  StaggerItem,
  InteractiveCard,
  MagneticButton,
  FloatingElement,
  GSAPCounter,
} from '@/components/ui/OptimizedAnimations';
import {
  CharacterStagger,
  SparklesCore,
  Spotlight,
} from '@/components/ui/AceternityAnimations';

const features = [
  {
    icon: Users,
    title: 'Admission Management',
    description: 'Streamlined online admissions with application tracking, document verification, and automated communication.',
  },
  {
    icon: CreditCard,
    title: 'Fee & Finance',
    description: 'Complete fee management with online payments, installment plans, and automated reminders.',
  },
  {
    icon: Calendar,
    title: 'Smart Attendance',
    description: 'AI-powered attendance with biometric integration, real-time alerts, and comprehensive reports.',
  },
  {
    icon: Bell,
    title: 'Parent Portal',
    description: 'Keep parents engaged with real-time updates, homework tracking, and direct teacher communication.',
  },
  {
    icon: ClipboardList,
    title: 'Examination System',
    description: 'End-to-end exam management from scheduling to result publication with analytics.',
  },
  {
    icon: Bus,
    title: 'Transport Management',
    description: 'GPS tracking, route optimization, and real-time notifications for school transportation.',
  },
];

const modules = [
  { name: 'Student Information System', icon: Users },
  { name: 'Timetable Management', icon: Calendar },
  { name: 'Library Management', icon: BookOpen },
  { name: 'Inventory & Assets', icon: ClipboardList },
  { name: 'HR & Payroll', icon: CreditCard },
  { name: 'Reports & Analytics', icon: BarChart3 },
];

const stats = [
  { value: '300', suffix: '+', label: 'Schools Using' },
  { value: '500', suffix: 'K+', label: 'Students Managed' },
  { value: '99.9', suffix: '%', label: 'Uptime' },
  { value: '24', suffix: '/7', label: 'Support' },
];

const benefits = [
  { icon: Clock, title: 'Save 15+ Hours Weekly', desc: 'Automate repetitive administrative tasks' },
  { icon: Shield, title: 'Data Security', desc: 'ISO 27001 certified with AES-256 encryption' },
  { icon: Globe, title: 'Access Anywhere', desc: 'Cloud-based system works on any device' },
  { icon: Zap, title: 'Quick Setup', desc: 'Go live in under 2 weeks with our team' },
];

const faqs = [
  {
    question: 'How long does implementation take?',
    answer: 'Most schools are fully operational within 2-3 weeks. Our implementation team handles data migration, training, and customization.',
  },
  {
    question: 'Can we migrate data from our existing system?',
    answer: 'Yes, we support data migration from all major school management systems. Our team handles the entire process securely.',
  },
  {
    question: 'Is training provided for staff?',
    answer: 'Comprehensive training is included for administrators, teachers, and staff. We also provide ongoing support and documentation.',
  },
  {
    question: 'What about data security and backups?',
    answer: 'Your data is encrypted at rest and in transit. We perform daily backups with 30-day retention and have disaster recovery protocols in place.',
  },
];

const FAQItem = ({ faq, isOpen, onToggle }) => (
  <motion.div className="glass-card overflow-hidden">
    <button
      onClick={onToggle}
      className="w-full p-6 flex items-center justify-between text-left hover:bg-glass-hover transition-colors"
    >
      <span className="text-foreground font-medium pr-4">{faq.question}</span>
      <motion.div
        animate={{ rotate: isOpen ? 180 : 0 }}
        transition={{ duration: 0.3 }}
      >
        <ChevronDown className="w-5 h-5 text-teal flex-shrink-0" />
      </motion.div>
    </button>
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="px-6 pb-6 text-muted-foreground">
            {faq.answer}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </motion.div>
);

const SchoolERPPage = () => {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="relative bg-void min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        <Spotlight className="-top-40 left-0 md:left-60 md:-top-20" fill="hsl(166, 100%, 37%)" />

        <div className="absolute inset-0 z-[1]">
          <SparklesCore
            id="schoolErpSparkles"
            background="transparent"
            minSize={0.4}
            maxSize={1}
            particleDensity={30}
            particleColor="#00BFA5"
          />
        </div>

        <FloatingElement duration={8} distance={20} className="absolute top-20 right-10 opacity-10">
          <div className="w-64 h-64 border border-teal/30 rounded-full" />
        </FloatingElement>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <ScrollReveal delay={0.1}>
                <Link to="/products" className="inline-flex items-center gap-2 text-teal text-sm mb-6 hover:underline">
                  <ArrowRight className="w-4 h-4 rotate-180" />
                  Back to Products
                </Link>
              </ScrollReveal>

              <ScrollReveal delay={0.2}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-teal/20 flex items-center justify-center">
                    <School className="w-6 h-6 text-teal" />
                  </div>
                  <span className="px-3 py-1 rounded-full bg-teal/10 text-teal text-xs font-mono">
                    K-12 Schools
                  </span>
                </div>
              </ScrollReveal>

              <h1 className="text-4xl sm:text-5xl md:text-6xl font-display font-bold text-foreground mb-4">
                <CharacterStagger
                  text="School ERP"
                  delay={0.3}
                  staggerDelay={0.03}
                />
              </h1>
              <h2 className="text-2xl sm:text-3xl font-display text-gradient-teal mb-6">
                Complete K-12 Management System
              </h2>

              <ScrollReveal delay={0.6}>
                <p className="text-muted-foreground text-lg mb-8 max-w-xl">
                  Transform your school operations with AI-powered analytics, automated workflows,
                  and seamless parent-teacher communication. Trusted by 300+ schools worldwide.
                </p>
              </ScrollReveal>

              <ScrollReveal delay={0.8}>
                <div className="flex flex-col sm:flex-row gap-4">
                  <MagneticButton strength={0.2}>
                    <Link to="/contact">
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="btn-primary px-8 py-4"
                      >
                        Book a Demo
                        <ArrowRight className="w-5 h-5" />
                      </motion.button>
                    </Link>
                  </MagneticButton>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="btn-secondary px-8 py-4"
                  >
                    <Play className="w-5 h-5" />
                    Watch Overview
                  </motion.button>
                </div>
              </ScrollReveal>
            </div>

            {/* Stats Card */}
            <ScrollReveal direction="right" delay={0.4}>
              <div className="glass-card p-8">
                <h3 className="text-display text-xl text-foreground mb-6">
                  Trusted by Schools Worldwide
                </h3>
                <div className="grid grid-cols-2 gap-6">
                  {stats.map((stat, index) => (
                    <div key={stat.label} className="text-center">
                      <div className="text-3xl font-display font-bold text-teal">
                        <GSAPCounter value={stat.value} suffix={stat.suffix} duration={2} />
                      </div>
                      <div className="text-sm text-muted-foreground">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-void-light/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal className="text-center mb-16">
            <span className="font-mono text-xs text-teal tracking-widest uppercase mb-4 block">
              Core Features
            </span>
            <h2 className="text-display text-3xl sm:text-4xl text-foreground mb-4">
              Everything You Need to<br />
              <span className="text-gradient-teal">Manage Your School</span>
            </h2>
          </ScrollReveal>

          <StaggerContainer staggerDelay={0.1} className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature) => (
              <StaggerItem key={feature.title}>
                <InteractiveCard hoverScale={1.02} hoverY={-6} glowColor="rgba(0,191,165,0.2)">
                  <div className="glass-card p-6 h-full">
                    <div className="w-12 h-12 rounded-xl bg-teal/20 flex items-center justify-center mb-4">
                      <feature.icon className="w-6 h-6 text-teal" />
                    </div>
                    <h3 className="text-display text-lg text-foreground mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {feature.description}
                    </p>
                  </div>
                </InteractiveCard>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Modules Section */}
      <section className="py-24 bg-void">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <ScrollReveal direction="left">
              <span className="font-mono text-xs text-orange tracking-widest uppercase mb-4 block">
                Complete Suite
              </span>
              <h2 className="text-display text-3xl sm:text-4xl text-foreground mb-6">
                Integrated Modules for<br />
                <span className="text-gradient-orange">Every Department</span>
              </h2>
              <p className="text-muted-foreground mb-8">
                Our modular architecture lets you start with what you need and expand as you grow.
                All modules share a unified database for seamless data flow.
              </p>
            </ScrollReveal>

            <ScrollReveal direction="right">
              <div className="grid grid-cols-2 gap-4">
                {modules.map((module, index) => (
                  <motion.div
                    key={module.name}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    viewport={{ once: true }}
                    whileHover={{ scale: 1.02, y: -4 }}
                    className="glass-card p-4 flex items-center gap-3"
                  >
                    <div className="w-10 h-10 rounded-lg bg-orange/20 flex items-center justify-center flex-shrink-0">
                      <module.icon className="w-5 h-5 text-orange" />
                    </div>
                    <span className="text-sm font-medium text-foreground">{module.name}</span>
                  </motion.div>
                ))}
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 bg-void-light/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal className="text-center mb-16">
            <h2 className="text-display text-3xl sm:text-4xl text-foreground">
              Why Schools Choose <span className="text-gradient-teal">Tattva</span>
            </h2>
          </ScrollReveal>

          <StaggerContainer staggerDelay={0.1} className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit) => (
              <StaggerItem key={benefit.title}>
                <InteractiveCard hoverScale={1.03} hoverY={-6} glowColor="rgba(0,191,165,0.2)">
                  <div className="glass-card p-6 text-center h-full">
                    <div className="w-14 h-14 rounded-2xl bg-teal/20 flex items-center justify-center mx-auto mb-4">
                      <benefit.icon className="w-7 h-7 text-teal" />
                    </div>
                    <h4 className="text-display text-lg text-foreground mb-2">{benefit.title}</h4>
                    <p className="text-sm text-muted-foreground">{benefit.desc}</p>
                  </div>
                </InteractiveCard>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-void">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal className="text-center mb-12">
            <span className="font-mono text-xs text-teal tracking-widest uppercase mb-4 block">
              Common Questions
            </span>
            <h2 className="text-display text-3xl text-foreground">
              Frequently Asked <span className="text-gradient-teal">Questions</span>
            </h2>
          </ScrollReveal>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <ScrollReveal key={index} delay={index * 0.1}>
                <FAQItem
                  faq={faq}
                  isOpen={openFaq === index}
                  onToggle={() => setOpenFaq(openFaq === index ? null : index)}
                />
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-void-light/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <ScrollReveal>
            <h2 className="text-display text-3xl sm:text-4xl text-foreground mb-6">
              Ready to Transform<br />
              <span className="text-gradient-teal">Your School?</span>
            </h2>
            <p className="text-muted-foreground text-lg mb-8 max-w-2xl mx-auto">
              Join 300+ schools that have modernized their operations with our School ERP.
              Schedule a personalized demo to see how it works for your institution.
            </p>
            <MagneticButton strength={0.2}>
              <Link to="/contact">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="btn-primary px-10 py-5 text-lg"
                >
                  Book Your Free Demo
                  <ArrowRight className="w-5 h-5" />
                </motion.button>
              </Link>
            </MagneticButton>
          </ScrollReveal>
        </div>
      </section>

      {/* Explore Suite Section */}
      <section className="py-16 bg-void border-t border-glass-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal className="text-center mb-8">
            <h3 className="text-display text-xl text-foreground">
              Explore Our <span className="text-gradient-orange">Suite</span>
            </h3>
          </ScrollReveal>

          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { name: 'College ERP', icon: GraduationCap, href: '/products/college-erp', color: 'orange' },
              { name: 'Lynkt', icon: Users, href: '/products/lynkt', color: 'teal' },
              { name: 'LMS', icon: BookOpen, href: '/products/lms', color: 'orange' },
            ].map((product) => (
              <Link key={product.name} to={product.href}>
                <motion.div
                  whileHover={{ scale: 1.02, y: -4 }}
                  className={cn(
                    'glass-card p-6 flex items-center gap-4 cursor-pointer',
                    'border transition-colors duration-300',
                    product.color === 'teal' ? 'hover:border-teal/40' : 'hover:border-orange/40'
                  )}
                >
                  <div className={cn(
                    'w-12 h-12 rounded-xl flex items-center justify-center',
                    product.color === 'teal' ? 'bg-teal/20' : 'bg-orange/20'
                  )}>
                    <product.icon className={cn(
                      'w-6 h-6',
                      product.color === 'teal' ? 'text-teal' : 'text-orange'
                    )} />
                  </div>
                  <div>
                    <h4 className="text-display text-foreground">{product.name}</h4>
                    <p className="text-xs text-muted-foreground">Explore product</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-muted-foreground ml-auto" />
                </motion.div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default SchoolERPPage;
